import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import OnboardingService from '../../../services/onboarding.service';
import { fetchCompanyFilters, getFilterSellers } from '../../../store/actions/rateCard';
import { getSelectList } from '../../../config/utils';

// Custom hook for sorting and filtering
export const useSortAndFilter = (data, sortColumn, sortOrder, setSortColumn, setSortOrder) => {
    const handleSortingColumn = useCallback(
        (event, column) => {
            event.preventDefault();
            const newSortOrder = sortColumn === column && sortOrder === 'ASCENDING' ? 'DESCENDING' : 'ASCENDING';
            setSortColumn(column);
            setSortOrder(newSortOrder);
        },
        [sortColumn, sortOrder, setSortColumn, setSortOrder]
    );

    const processedData = useMemo(() => {
        let sortedData = [...data];

        if (sortColumn) {
            sortedData.sort((a, b) => {
                let aVal = a[sortColumn];
                let bVal = b[sortColumn];

                if (aVal === null || aVal === undefined) aVal = '';
                if (bVal === null || bVal === undefined) bVal = '';

                if (typeof aVal === 'string' || typeof bVal === 'string') {
                    aVal = String(aVal).toLowerCase();
                    bVal = String(bVal).toLowerCase();
                    return sortOrder === 'ASCENDING' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
                }

                return sortOrder === 'ASCENDING' ? aVal - bVal : bVal - aVal;
            });
        }

        return sortedData;
    }, [data, sortColumn, sortOrder]);

    return {
        processedData,
        handleSortingColumn,
        sortColumn,
        sortOrder,
    };
};

// Custom hook for pagination
export const usePagination = (data, initialPageSize = 30) => {
    const [numberOfPage, setNumberOfPage] = useState(initialPageSize);
    const [currentPage, setCurrentPage] = useState(1);

    const totalPages = useMemo(() => Math.ceil(data.length / numberOfPage), [data.length, numberOfPage]);
    const startIdx = useMemo(() => (currentPage - 1) * numberOfPage, [currentPage, numberOfPage]);
    const endIdx = useMemo(() => startIdx + numberOfPage, [startIdx, numberOfPage]);
    const paginatedData = useMemo(() => data.slice(startIdx, endIdx), [data, startIdx, endIdx]);

    return {
        numberOfPage,
        setNumberOfPage,
        currentPage,
        setCurrentPage,
        totalPages,
        startIdx,
        paginatedData,
    };
};

// Custom hook for affiliate list management (data fetching + pagination + search + filter)
export const useAffiliateList = () => {
    const [affiliateData, setAffiliateData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPageIndex, setCurrentPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(30);
    const [refreshKey, setRefreshKey] = useState(0);
    const [searchKeywords, setSearchKeywords] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [companyFilter, setCompanyFilter] = useState([]);
    const [sellerFilter, setSellerFilter] = useState([]);
    const debounceTimerRef = useRef(null);

    // Debounce search keywords (≥ 300 ms as per spec)
    useEffect(() => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => {
            setDebouncedSearch(searchKeywords);
            setCurrentPageIndex(1);
        }, 300);
        return () => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, [searchKeywords]);

    // Commit search immediately (skips debounce). Used by Enter / Search / Reset —
    // always bumps `refreshKey` so the API refires even when the value hasn't
    // changed since the last debounce commit.
    const flushSearch = useCallback((valueOverride) => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        const commit = valueOverride !== undefined ? valueOverride : searchKeywords;
        if (valueOverride !== undefined) setSearchKeywords(valueOverride);
        setDebouncedSearch(commit);
        setCurrentPageIndex(1);
        setRefreshKey(k => k + 1);
    }, [searchKeywords]);

    const companyIds = useMemo(
        () => (companyFilter || [])
            .map(opt => opt?.Id ?? opt?.value ?? opt?.id ?? opt)
            .filter(v => v !== undefined && v !== null),
        [companyFilter]
    );
    const sellerIds = useMemo(
        () => (sellerFilter || [])
            .map(opt => opt?.id ?? opt?.value ?? opt)
            .filter(v => v !== undefined && v !== null),
        [sellerFilter]
    );
    const companyIdsKey = companyIds.join(',');
    const sellerIdsKey = sellerIds.join(',');

    useEffect(() => {
        setLoading(true);
        OnboardingService.getInvitations(currentPageIndex, pageSize, {
            companyIds,
            sellerIds,
            searchKeywords: debouncedSearch,
        })
            .then(response => {
                if (response?.data?.Status === 200 && response?.data?.EntityList) {
                    setAffiliateData(response.data.EntityList);
                    setTotalRecords(response.data.TotalRecords || 0);
                } else {
                    toast.error('Failed to fetch invitations');
                    setAffiliateData([]);
                }
            })
            .catch(error => {
                console.error('Error fetching invitations:', error);
                toast.error('Error fetching invitations');
                setAffiliateData([]);
            })
            .finally(() => {
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPageIndex, pageSize, refreshKey, debouncedSearch, companyIdsKey, sellerIdsKey]);

    return {
        affiliateData,
        loading,
        totalRecords,
        currentPageIndex,
        setCurrentPageIndex,
        pageSize,
        setPageSize,
        refreshKey,
        setRefreshKey,
        searchKeywords,
        setSearchKeywords,
        flushSearch,
        companyFilter,
        setCompanyFilter,
        sellerFilter,
        setSellerFilter,
    };
};

// Custom hook for affiliate actions (CRUD operations)
export const useAffiliateActions = ({ setShowAddAffiliatePopup, setCurrentPageIndex, setRefreshKey }) => {
    const handleAddAffiliate = useCallback(formData => {
        return OnboardingService.createInvitation(formData.email)
            .then(response => {
                if (response?.data?.Status === 200) {
                    const successMessage = formData.email
                        ? 'Affiliate invitation sent successfully'
                        : 'Affiliate invitation created';
                    toast.success(successMessage);
                    setShowAddAffiliatePopup(false);
                    // Refresh invitations list
                    setCurrentPageIndex(1);
                    setRefreshKey(prev => prev + 1);
                } else {
                    toast.error(response?.data?.errorDescription || 'Failed to send invitation');
                    throw new Error(response?.data?.errorDescription || 'Failed to send invitation');
                }
            })
            .catch(error => {
                const errorMessage = error?.response?.data?.errorDescription || 'Error sending invitation';
                toast.error(errorMessage);
                throw error;
            });
    }, [setShowAddAffiliatePopup, setCurrentPageIndex, setRefreshKey]);

    const handleDeleteInvitation = useCallback(inviteId => {
        OnboardingService.deleteInvitation(inviteId)
            .then(response => {
                if (response?.data?.Status === 200) {
                    toast.success('Invitation deleted successfully');
                    // Refresh current page
                    setRefreshKey(prev => prev + 1);
                } else {
                    toast.error(response?.data?.errorDescription || 'Failed to delete invitation');
                }
            })
            .catch(error => {
                const errorMessage = error?.response?.data?.errorDescription || 'Error deleting invitation';
                toast.error(errorMessage);
            });
    }, [setRefreshKey]);

    const handleCopyInvitationUrl = useCallback(async (e, item) => {
        e.preventDefault();
        try {
            const url = item?.InvitationUrl;
            if (url) {
                await navigator.clipboard.writeText(url);
                toast.success('Invitation URL Copied');
            } else {
                toast.error('No URL available to copy');
            }
        } catch {
            toast.error('Failed to copy URL');
        }
    }, []);

    return {
        handleAddAffiliate,
        handleDeleteInvitation,
        handleCopyInvitationUrl,
    };
};

// Hook: load CompanySeller filter options (super-admin only). Reuses the same
// company/seller filter feeds used by Carrier Connect. Options are shaped as
// { value, label } where value is treated as the CompanySellerId for the
// invitations API (per product direction to reuse the Carrier Connect filter).
export const useCompanySellerFilterOptions = (enabled) => {
    const dispatch = useDispatch();
    const { filterAllSellers } = useSelector(state => state.RateCardReducer);
    const [options, setOptions] = useState([]);
    const requested = useRef(false);

    useEffect(() => {
        if (!enabled || requested.current) return;
        requested.current = true;
        dispatch(fetchCompanyFilters({ pageIndex: 1, pageSize: 10, sortColumn: 'name', sortOrder: 'ascending' }));
        dispatch(getFilterSellers({
            pageIndex: 1,
            pageSize: 10,
            sortColumn: 'name',
            sortOrder: 'ASCENDING',
            companyIdList: [0],
            screenType: 2,
        }));
    }, [enabled, dispatch]);

    useEffect(() => {
        const list = filterAllSellers?.EntityList || [];
        if (list.length) {
            setOptions(getSelectList(list, 'id', 'sellerAndCompany'));
        }
    }, [filterAllSellers]);

    return options;
};

// Hook: drives the drill-down invitation-referrals modal.
export const useInvitationReferrals = (companySellerId, isOpen) => {
    const [referralData, setReferralData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const [currentPageIndex, setCurrentPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(30);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        if (!isOpen || !companySellerId) return;
        setLoading(true);
        OnboardingService.getInvitationReferrals(companySellerId, currentPageIndex, pageSize)
            .then(response => {
                if (response?.data?.Status === 200) {
                    setReferralData(response.data.EntityList || []);
                    setTotalRecords(response.data.TotalRecords || 0);
                } else if (response?.data?.Status === 403) {
                    toast.error('You do not have permission to view referrals');
                    setReferralData([]);
                } else {
                    toast.error('Failed to fetch referrals');
                    setReferralData([]);
                }
            })
            .catch(error => {
                if (error?.response?.status === 403) {
                    toast.error('You do not have permission to view referrals');
                } else {
                    toast.error('Error fetching referrals');
                }
                setReferralData([]);
            })
            .finally(() => setLoading(false));
    }, [isOpen, companySellerId, currentPageIndex, pageSize, refreshKey]);

    // Reset page whenever the modal is (re)opened for a different CompanySeller
    useEffect(() => {
        if (isOpen) {
            setCurrentPageIndex(1);
        }
    }, [isOpen, companySellerId]);

    const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

    return {
        referralData,
        loading,
        totalRecords,
        currentPageIndex,
        setCurrentPageIndex,
        pageSize,
        setPageSize,
        refresh,
    };
};
